/**
 * / — Home. A PAGE; AppLayoutRoute supplies the chrome.
 *
 * The pitch: running software means juggling a pile of separate subscriptions
 * and a job nobody on the team is hired to do. Bloomneo is one platform that
 * holds the lot, on one monthly bill.
 *
 * ── Why the page talks about a platform and not about us ──────────────────
 * It used to do the opposite. "A developer on call", "the people who patch
 * it", "kept current by people who do this daily", "operated for you" - a
 * reader met five of those before the first screenshot, and a page written in
 * the first person plural describes an agency no matter what it is selling.
 * The modules in PLATFORM_MODULES are all live and all self-serve, so the
 * honest frame is the one that says so. Support still appears, because every
 * product has support; what is gone is support as the product.
 *
 * The coverage grid reads from PLATFORM_MODULES — the same catalog the product
 * uses — so this page cannot claim more than what is actually built.
 *
 * ── What changed in the port ──────────────────────────────────────────────
 * This replaces the Bloom scaffold's demo landing page ("Bloomneo is running.
 * Now make it yours."), which was never meant to survive first contact with a
 * real product and had been sitting on / since the migration.
 *
 * The page no longer draws its own header, footer or <main>; the layout route
 * owns all three, and its <main> is already max-w-6xl with padding, so the
 * inner max-w-7xl wrappers are gone rather than nested. `<Reveal>` — a no-op
 * stub from a removed motion library — is now `bloom-rise`, the project's own
 * CSS animation.
 */
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@bloomneo/uikit';
import {
  ArrowRight, Check, ShieldCheck, Gauge, Activity, UserMinus,
  DatabaseBackup, RefreshCw, KeyRound, Rocket, Plus, Receipt,
} from 'lucide-react';
import { SEO } from '@/shared/SEO';
import { ProductTour } from '../components/ProductTour';
import { PLATFORM_MODULES } from '@/shared/config/platform-modules';

/**
 * What the platform actually does, stated in specifics.
 *
 * The page said "everything is backed up, and watched", which is the sentence
 * every managed host writes. These are the schedules and numbers behind it, and
 * they are the part a reader can check us against later - which is the only
 * reason they are worth printing.
 */
const OPERATIONS = [
  { icon: DatabaseBackup, label: 'Database backups', detail: 'Every database dumped nightly at 02:30 UTC to offsite object storage, kept 30 days.' },
  { icon: ShieldCheck, label: 'Certificates', detail: 'Issued and renewed automatically, with expiry tracked per domain — the renewal is not a reminder in someone\'s calendar.' },
  { icon: Activity, label: 'Uptime', detail: 'Every site checked continuously from outside the server, with alerts when a check changes state rather than on every blip.' },
  { icon: RefreshCw, label: 'Patching', detail: 'Operating system and security updates applied on every server in your account on a schedule, with reboots planned rather than deferred indefinitely.' },
  { icon: KeyRound, label: 'Access', detail: 'Key-only SSH, a firewall on every host, and repeated-failure banning — the baseline a public IP needs within hours of existing.' },
  { icon: Rocket, label: 'Releases', detail: 'Deploys keep the previous release on disk and health-check the new one before it takes traffic, so a bad deploy is a rollback rather than an outage.' },
];

/**
 * Moving in, which is the part everyone is actually nervous about.
 *
 * Written as three things that happen to the infrastructure rather than three
 * things we do - "week one we take inventory / then we take over / then it is
 * ours" read as the phases of a consulting engagement, which is the single
 * most agency-sounding block the page had.
 */
const HANDOVER = [
  {
    step: 'Add what you already have',
    body: 'Existing domains, certificates and servers go in first, and the dashboard shows what is actually there — which certificates expire when, what is not backed up, where each thing runs. Most of that is unknown at the start; seeing it in one list is the point.',
  },
  {
    step: 'Move it across, one piece at a time',
    body: 'DNS, certificates, backups and monitoring move first because they change nothing about how the application runs. The server move comes after, with the old one still standing.',
  },
  {
    step: 'Then it runs on a schedule',
    body: 'Patching, renewals, backups and alerts become a schedule on the platform rather than a reminder in somebody\'s calendar. Your login shows every one of them running.',
  },
];

/**
 * Questions people actually ask before signing. Rendered on the page and, as
 * FAQPage schema, in the head - the same answers either way, which is the only
 * honest way to publish structured data.
 */
const FAQ = [
  {
    q: 'Do we have to move servers to use Bloomneo?',
    a: 'No. DNS, certificates, backups and monitoring can run against infrastructure you already have. Moving the servers is a separate decision, and what the dashboard shows you in the first week is usually what informs it.',
  },
  {
    q: 'What happens if the site goes down at 2am?',
    a: 'The check that notices it runs from outside your server, so it does not depend on the thing that failed. It alerts on a change of state, and the response times in our service level agreement are what we hold ourselves to.',
  },
  {
    q: 'Are the backups tested, or just taken?',
    a: 'Taken nightly, kept 30 days offsite, and restored to prove they work. A backup nobody has restored is a hope, not a backup — the difference only shows on the day it matters.',
  },
  {
    q: 'Can we leave, and take everything with us?',
    a: 'Yes. Domains stay registered to you, the data is yours, and servers, DNS and backups are exported on request. Nothing here is designed to make leaving expensive.',
  },
  {
    q: 'Do you replace our developers?',
    a: 'No. Bloomneo is the layer underneath what they build — servers, databases, certificates, backups, monitoring. They keep shipping; they stop being the person who remembers to renew a certificate.',
  },
  {
    q: 'What does it cost?',
    a: 'Packages start at $50 a month. Each one is listed on the pricing page with what it includes and the limits it carries, so you can pick one at sign-up and change it from the dashboard later.',
  },
];

/** What a business pays for separately to keep one application running. */
const THE_PILE = [
  'Cloud servers',
  'SSL certificates',
  'Database hosting',
  'Backups',
  'Object storage',
  'Transactional email',
  'Uptime monitoring',
  'Deployment tooling',
];

const OUTCOMES = [
  {
    icon: UserMinus,
    title: 'No DevOps hire',
    body: 'Patching, certificate renewal, backup verification and database tuning are built into the platform and run on a schedule. None of it is a part-time job somebody picks up between features, which is where the gaps come from.',
  },
  {
    icon: ShieldCheck,
    title: 'Security is handled',
    body: 'Firewalls, key-only SSH, repeated-failure banning and operating-system updates are applied to every server in your account on a schedule — not deferred until an audit or an incident asks for them.',
  },
  {
    icon: Gauge,
    title: 'Scales with the traffic',
    body: 'Servers are sized, tuned and scaled out as volume grows. A launch, a campaign or an exam-day spike is a capacity change planned in advance rather than one discovered at 2am.',
  },
  {
    icon: Activity,
    title: 'Everything is backed up, and watched',
    body: 'Every database is dumped nightly to offsite storage and uptime is checked continuously. Both are verified — a backup nobody has restored and an alert nobody receives are the same thing as neither.',
  },
];

/**
 * The closing block's illustration: three machines, watched.
 *
 * Everything is `currentColor` at varying opacity, so it inherits
 * primary-foreground from the block it sits in and cannot drift out of the
 * palette the way a hardcoded fill would. No text: the heading beside it is
 * already saying the thing, and a diagram that repeats the sentence is noise.
 *
 * The pulse is the point of it - a stack anyone can draw, being continuously
 * checked is what is actually being sold. Motion stops under
 * prefers-reduced-motion, like the hero.
 */
function WatchedStack({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 220 180" className={className} fill="none"
      role="img" aria-label="Three servers under continuous monitoring"
    >
      {/* the machines */}
      {[0, 1, 2].map((i) => (
        <g key={i} opacity={0.92 - i * 0.14}>
          <rect
            x="16" y={34 + i * 44} width="132" height="34" rx="9"
            fill="currentColor" fillOpacity="0.10"
            stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.5"
          />
          <circle cx="34" cy={51 + i * 44} r="3.5" fill="currentColor" fillOpacity="0.9" />
          <rect x="48" y={47 + i * 44} width="52" height="3" rx="1.5" fill="currentColor" fillOpacity="0.5" />
          <rect x="48" y={54 + i * 44} width="30" height="3" rx="1.5" fill="currentColor" fillOpacity="0.28" />
        </g>
      ))}

      {/* the watch: a pulse running the height of the stack */}
      <path
        d="M168 42 L168 96 L176 96 L181 78 L189 118 L195 96 L204 96 L204 150"
        stroke="currentColor" strokeOpacity="0.75" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      />
      <circle cx="189" cy="118" r="4" fill="currentColor">
        <animate
          attributeName="fill-opacity" values="1;0.25;1" dur="2.4s"
          repeatCount="indefinite" className="bloom-flow"
        />
      </circle>

      {/* what the watching is for */}
      <path
        d="M168 20 l12 -6 12 6 v10 c0 8 -5 13 -12 16 -7 -3 -12 -8 -12 -16 z"
        fill="currentColor" fillOpacity="0.14"
        stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.5"
      />
      <path d="M174 28 l4 4 8 -8" stroke="currentColor" strokeOpacity="0.9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * The headline promises a "layer", so the diagram shows one: the customer's
 * software on top, the operated layer beneath it, hardware at the base.
 *
 * ── Why it looks the way it does ──────────────────────────────────────────
 * The first version drew three flat rectangles joined by grey dashes, which
 * read as a stack of boxes rather than a thing holding another thing up. The
 * middle band is the product, so it is the only element with weight: a gradient
 * fill, a soft glow, and a gold rule that picks up the mark in the logo.
 *
 * The connectors flow downward, which is what turns "three boxes" into "this
 * runs on that". Motion stops entirely under prefers-reduced-motion.
 *
 * Every colour is a theme token, so it follows light, dark and any future
 * rebrand without being touched.
 */
function LayerDiagram() {
  const services = [
    ['Servers', 'Database', 'Storage'],
    ['Backups', 'Security', 'Monitoring'],
  ];

  return (
    <svg
      viewBox="0 0 400 320"
      role="img"
      aria-label="Your software runs on a layer of infrastructure that the Bloomneo platform runs"
      className="w-full h-auto overflow-visible"
    >
      <defs>
        <linearGradient id="operated-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.16" />
          <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.05" />
        </linearGradient>
        {/*
          * userSpaceOnUse, not the default objectBoundingBox.
          *
          * These connectors are vertical lines, so their bounding box is zero
          * WIDE. A gradient measured against that box degenerates and the stroke
          * paints nothing at all — the lines were in the DOM, correct, and
          * completely invisible. Absolute coordinates avoid the box entirely.
          */}
        <linearGradient id="connector" gradientUnits="userSpaceOnUse" x1="0" y1="70" x2="0" y2="268">
          <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.30" />
          <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.85" />
        </linearGradient>
        <filter id="lift" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="3" stdDeviation="6"
            floodColor="var(--color-primary)" floodOpacity="0.14" />
        </filter>
        <filter id="card-lift" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="4"
            floodColor="#000" floodOpacity="0.10" />
        </filter>
      </defs>

      {/* ── Your software ── */}
      <g filter="url(#card-lift)">
        <rect
          x="64" y="6" width="272" height="64" rx="14"
          fill="var(--color-card)" stroke="var(--color-border)" strokeWidth="1.25"
        />
      </g>
      <text
        x="200" y="33" textAnchor="middle"
        fill="var(--color-foreground)" fontSize="14.5" fontWeight="600" fontFamily="inherit"
      >
        Your software
      </text>
      <text
        x="200" y="52" textAnchor="middle"
        fill="var(--color-muted-foreground)" fontSize="11" fontFamily="inherit"
      >
        the thing your business actually sells
      </text>

      {/* ── Flow into the operated layer ── */}
      {[120, 200, 280].map((x, i) => (
        <line
          key={x} x1={x} y1="70" x2={x} y2="100"
          stroke="url(#connector)" strokeWidth="2" strokeLinecap="round"
          strokeDasharray="4 6" className="bloom-flow"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}

      {/* ── The operated layer: the only element with weight ── */}
      <g filter="url(#lift)">
        <rect
          x="8" y="100" width="384" height="142" rx="18"
          fill="url(#operated-fill)"
          stroke="var(--color-primary)" strokeOpacity="0.4" strokeWidth="1.5"
        />
      </g>

      {/* Gold rule — the one place the logo's colour appears in the diagram */}
      <rect x="26" y="118" width="26" height="2.5" rx="1.25" fill="var(--color-brand-mark)" />
      <text
        x="60" y="123" fill="var(--color-primary)" fontSize="10"
        fontWeight="700" letterSpacing="1.5" fontFamily="inherit"
      >
        THE BLOOMNEO PLATFORM
      </text>

      {services.map((row, rowIndex) =>
        row.map((label, colIndex) => {
          const x = 26 + colIndex * 120;
          const y = 140 + rowIndex * 46;
          return (
            <g key={label}>
              <rect
                x={x} y={y} width="108" height="36" rx="9"
                fill="var(--color-background)"
                stroke="var(--color-primary)" strokeOpacity="0.28" strokeWidth="1.25"
              />
              <circle cx={x + 16} cy={y + 18} r="3.5" fill="var(--color-brand-mark)" />
              <text
                x={x + 28} y={y + 22}
                fill="var(--color-foreground)" fontSize="11.5" fontFamily="inherit"
              >
                {label}
              </text>
            </g>
          );
        })
      )}

      {/* ── Down to the metal ── */}
      {[120, 200, 280].map((x, i) => (
        <line
          key={x} x1={x} y1="242" x2={x} y2="268"
          stroke="url(#connector)" strokeWidth="2" strokeLinecap="round"
          strokeDasharray="4 6" className="bloom-flow"
          style={{ animationDelay: `${i * 0.18 + 0.09}s` }}
        />
      ))}
      <rect
        x="64" y="268" width="272" height="46" rx="14"
        fill="var(--color-muted)" stroke="var(--color-border)" strokeWidth="1.25"
      />
      <text
        x="200" y="296" textAnchor="middle"
        fill="var(--color-muted-foreground)" fontSize="12" fontFamily="inherit"
      >
        Our servers, in our data centre
      </text>
    </svg>
  );
}

export default function HomePage() {
  const navigate = useNavigate();

  return (
    /*
      One vertical rhythm for the whole page, set here rather than per section.
      Sections used to carry their own: two panels with no vertical padding at
      all, and two `py-16 md:py-24` sections between them. Where a 96px section
      met a 96px section the seam was ~192px of nothing, and where a panel met
      a panel there was none - so the page read as arbitrarily spaced rather
      than as a rhythm. A section added later inherits the spacing instead of
      picking some.

      flex+gap rather than space-y: the sections without padding let their
      children's margins collapse through, so identical 56px margins produced
      visible gaps of 44px and 68px. A flex gap is measured between boxes and
      cannot be collapsed into.
    */
    <div className="flex flex-col gap-14 md:gap-20">
      <SEO
        title="Bloomneo — All Your Infrastructure, One Subscription"
        description="Servers, domains, certificates, databases, backups, email and monitoring in one platform, on one monthly bill. Deployments, renewals and nightly backups run on a schedule."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────────
          The hero sat on a bare background, which is most of why the page read
          as flat: the diagram had nothing behind it and the headline had nothing
          holding it. Two drifting blooms — brand green and the logo's gold —
          plus a masked grid give it depth without putting anything in front of
          the words. All of it is aria-hidden and pointer-events-none; it is
          atmosphere, not content, and it stops moving under reduced-motion. */}
      {/*
        * `isolate` is load-bearing, not decoration.
        *
        * The ambient layer sits at -z-10. A `relative` element with z-index
        * auto does NOT create a stacking context, so that -10 escaped upward
        * and was painted in the ancestor's context — which put it behind the
        * app shell's opaque bg-background. The blooms were in the DOM, the
        * right size, animating, and completely invisible.
        *
        * isolation:isolate gives the section its own stacking context, so -10
        * means "behind this section's content" rather than "behind the page".
        */}
      <section className="relative isolate">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 bottom-0 left-1/2 -translate-x-1/2 w-screen -z-10 overflow-hidden"
          /*
           * Full-bleed, not inset-0.
           *
           * The shell's <main> is a centred max-w-6xl column, so an inset-0
           * ambient layer was only ever as wide as the text — the wash stopped
           * dead at the content edges while the page kept going, which is the
           * cut-off. Light behind a hero has to reach the window or it reads as
           * a panel someone forgot to size.
           *
           * left-1/2 + -translate-x-1/2 + w-screen breaks out of the column and
           * spans the viewport, and overflow-hidden keeps the blooms from
           * pushing the page wider than that.
           *
           * The same cut-off happened vertically. <main> has 40px of top
           * padding, so the layer began 40px below the header and left a hard
           * horizontal line across the page. -top-40 lifts it past that padding
           * and up behind the header, which is sticky and only 85% opaque — so
           * the wash reads through it rather than stopping underneath it.
           */
          style={{
            maskImage: 'radial-gradient(100% 115% at 50% 52%, black 26%, transparent 76%)',
            WebkitMaskImage: 'radial-gradient(100% 115% at 50% 52%, black 26%, transparent 76%)',
          }}
        >
          <div
            className="bloom-blob-a absolute left-[4%] -top-32 size-[46rem] rounded-full opacity-[0.34] blur-3xl dark:opacity-[0.30]"
            style={{ background: 'radial-gradient(circle, var(--color-primary), transparent 62%)' }}
          />
          <div
            className="bloom-blob-b absolute right-[6%] -top-24 size-[42rem] rounded-full opacity-[0.26] blur-3xl dark:opacity-[0.26]"
            style={{ background: 'radial-gradient(circle, var(--color-brand-mark), transparent 62%)' }}
          />
          <div
            className="absolute inset-0 opacity-[0.05] dark:opacity-[0.07]"
            style={{
              backgroundImage:
                'linear-gradient(var(--color-foreground) 1px, transparent 1px),' +
                'linear-gradient(90deg, var(--color-foreground) 1px, transparent 1px)',
              backgroundSize: '56px 56px',
              maskImage: 'radial-gradient(ellipse at 50% 0%, black 35%, transparent 72%)',
              WebkitMaskImage: 'radial-gradient(ellipse at 50% 0%, black 35%, transparent 72%)',
            }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-14 items-center pt-6 pb-14 md:pt-10 md:pb-20">
        <div className="bloom-rise">
          <div className="flex items-center gap-3 mb-7">
            <div className="h-px w-8 bg-primary" />
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-primary">
              Infrastructure Platform
            </span>
          </div>

          <h1
            className="font-bold tracking-[-0.03em] leading-[0.98] max-w-3xl"
            style={{ fontSize: 'clamp(2.1rem, 4.4vw, 3.4rem)' }}
          >
            The infrastructure layer your software runs on,{' '}
            <span className="text-primary">in one platform.</span>
          </h1>

          {/* Four things the platform does, in the order a reader meets them.
              This line previously ended "we watch the parts that cannot" — the
              only verb in the first screen belonged to us rather than to the
              product, which is the whole difference between a platform and an
              agency. */}
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
            One dashboard for the servers, domains, databases, certificates, backups and
            monitoring your software runs on. Deployments ship from it, certificates renew
            themselves, backups run nightly, and monitoring reaches you before your customers do.
          </p>

          {/*
            Get started leads.
            
            "Talk to us" was the primary action on a page whose whole argument
            is that this is a product you can simply have. A reader ready to
            buy was offered a conversation, and a reviewer checking whether
            customers can subscribe themselves found the same. The
            conversation stays, one step down, because on managed
            infrastructure plenty of people do want it first.
          */}
          <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-3">
            <Button size="lg" className="px-7 rounded-full gap-2" onClick={() => navigate('/auth/register')}>
              Get started <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate('/contact')}
            >
              Talk to us
            </Button>
            <Button
              size="lg"
              variant="ghost"
              className="text-muted-foreground rounded-full"
              onClick={() => navigate('/usecases')}
            >
              See what it replaces
            </Button>
          </div>
        </div>

          <div className="bloom-rise bloom-rise-2 hidden lg:block">
            <LayerDiagram />
          </div>
        </div>
      </section>

      {/* ── The mess ─────────────────────────────────────────────────────────
          A full-bleed band before the port. Inside the shell's max-w-6xl column
          it cannot bleed, so it is a panel. */}
      <section className="bloom-rise rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 md:p-10">
        <h2
          className="font-bold tracking-[-0.02em] leading-[1.05] max-w-2xl"
          style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
        >
          Running one application quietly turns into a procurement problem.
        </h2>
        <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed max-w-xl">
          None of it is optional, and none of it arrives bundled. It shows up one vendor at a
          time — each with its own invoice, its own login, and its own way of failing.
        </p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-8 items-stretch">
          {/* Before — the pile */}
          <div className="rounded-xl border border-border bg-background p-5">
            <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground mb-4">
              What you pay for today
            </p>
            {/*
              Chips rather than a row per line. Eight rows each ending in the
              same two words read as a table someone has to scan; scattered
              tokens read as what they are - a pile. Each carries the receipt
              mark, so the point survives without repeating "separate bill"
              eight times.
            */}
            <ul className="flex flex-wrap gap-2">
              {THE_PILE.map((item) => (
                <li
                  key={item}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground"
                >
                  <Receipt className="h-3 w-3 shrink-0 opacity-50" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-muted-foreground/70">
              {THE_PILE.length} vendors, {THE_PILE.length} invoices, {THE_PILE.length} logins.
            </p>
          </div>

          {/* Cards stack below lg and sit side by side above it, so the
              arrow has to turn with them. */}
          <div className="flex items-center justify-center text-muted-foreground/60">
            <ArrowRight className="h-6 w-6 rotate-90 lg:rotate-0 shrink-0" />
          </div>

          {/* After — one line */}
          <div className="flex flex-col justify-center rounded-xl border border-primary/30 bg-primary/5 p-5">
            <p className="text-xs font-medium tracking-wide uppercase text-primary mb-4">
              What you pay for with us
            </p>
            <div className="flex items-center justify-between gap-3 py-2 border-b border-primary/20">
              <span className="text-sm font-semibold">Bloomneo</span>
              {/* "One monthly BILL", not "one monthly plan". The claim is that
                  a single invoice covers the whole stack — but read beside a
                  pricing page offering three tiers, "one plan" says we sell
                  exactly one, which is the opposite of what is on it. */}
              <span className="text-xs text-muted-foreground shrink-0">one monthly bill</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mt-4">
              The same stack, in one platform that patches it, renews it, backs it up and watches
              it. One invoice, one login, one place to see all of it.
            </p>
          </div>
        </div>
      </section>

      {/* ── Outcomes ─────────────────────────────────────────────────────── */}
      <section>
        <div className="bloom-rise">
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.05] mb-3"
            style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
          >
            What you get
          </h2>
          <p className="text-muted-foreground max-w-xl mb-12">
            The platform runs the whole layer — so the work that keeps software online stops
            depending on somebody remembering to do it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {OUTCOMES.map((item, i) => (
            <div key={item.title} className={`bloom-rise bloom-rise-${Math.min(i + 1, 4)} flex gap-4`}>
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <item.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-base mb-1.5 tracking-tight">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── The dashboard ────────────────────────────────────────────────
          Every section above this one DESCRIBES the service. None of them
          showed it. A page that only describes reads as an agency however
          carefully it is written, and a reader deciding whether to hand over
          their infrastructure wants to see the thing they will be looking at.

          The screenshots come from a fictional customer — Harborline Studio,
          invented applications, reserved example domains — captured by
          scripts/product-shots.mjs rather than cropped by hand, so they can
          be regenerated when the product moves instead of quietly ageing.
          Both themes ship because the site has a dark mode and a light
          screenshot on a dark page reads as a hole.
      */}
      <section className="bloom-rise">
        <div className="max-w-2xl">
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.05] mb-3"
            style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
          >
            Your dashboard
          </h2>
          <p className="text-muted-foreground mb-8">
            One place for every application on the platform: what is deployed, which addresses it
            answers on, whether the certificates are current, and what your package covers.
            Deployments, domains and your package are all changed from these same screens.
          </p>
        </div>

        <ProductTour />
      </section>

      {/* ── What is bundled ──────────────────────────────────────────────── */}
      <section className="bloom-rise rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 md:p-10">
        <h2
          className="font-bold tracking-[-0.02em] leading-[1.05] mb-3"
          style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
        >
          What is in the bundle
        </h2>
        <p className="text-muted-foreground max-w-xl mb-10">
          Every one of these is part of the platform and included in your package. Nothing here is
          a bolt-on you discover a bill for later.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden">
          {PLATFORM_MODULES.filter((m) => !m.hideInBundle).map((module) => {
            const live = module.status === 'live';
            return (
              <div key={module.key} className="bg-background p-5 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-3">
                  <module.icon
                    className={`h-5 w-5 shrink-0 ${live ? 'text-primary' : 'text-muted-foreground/50'}`}
                  />
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-primary">
                    <Check className="h-3 w-3" />Included
                  </span>
                </div>
                <h3 className={`font-semibold text-sm tracking-tight ${live ? '' : 'text-muted-foreground'}`}>
                  {module.label}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{module.summary}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── The schedules ─────────────────────────────────────────────────
          Specifics, because the section above it makes claims and this is
          where they are cashed. */}
      <section>
        <div className="bloom-rise">
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.05] mb-3"
            style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
          >
            What runs on a schedule
          </h2>
          <p className="text-muted-foreground max-w-xl mb-10">
            Every host says your servers are looked after. These are the schedules behind it, with
            the times and the numbers you can hold us to.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {OPERATIONS.map((op, i) => (
            <div
              key={op.label}
              className={`bloom-rise bloom-rise-${Math.min(i + 1, 4)} group rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary/40`}
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                <op.icon className="h-4.5 w-4.5" />
              </span>
              <h3 className="mt-3.5 text-sm font-semibold tracking-tight">{op.label}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{op.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── The handover ─────────────────────────────────────────────────── */}
      <section className="bloom-rise rounded-2xl border border-border bg-muted/40 p-6 sm:p-8 md:p-10">
        <h2
          className="font-bold tracking-[-0.02em] leading-[1.05] max-w-2xl"
          style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
        >
          Moving what you already have is the part people worry about.
        </h2>
        <p className="mt-4 max-w-xl text-muted-foreground">
          So it happens in the order that risks least — the pieces that change nothing about how
          your application runs move first.
        </p>

        <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {HANDOVER.map((phase, i) => (
            <li key={phase.step} className={`bloom-rise bloom-rise-${i + 1}`}>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {i + 1}
              </span>
              <h3 className="mt-3 text-sm font-semibold tracking-tight">{phase.step}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{phase.body}</p>
            </li>
          ))}
        </ol>

        <p className="mt-8 text-sm text-muted-foreground">
          Five situations people arrive with, and what the first week looks like in each:{' '}
          <Link to="/usecases" className="font-medium text-primary underline-offset-4 hover:underline">
            read the use cases
          </Link>
          .
        </p>
      </section>

      {/* ── Questions ─────────────────────────────────────────────────────
          Also emitted as FAQPage schema above. Same answers in both places:
          publishing structured data that differs from the page is how a rich
          result becomes a penalty. */}
      <section>
        <div className="bloom-rise">
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.05] mb-3"
            style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
          >
            Questions we are asked first
          </h2>
        </div>

        {/*
          <details> rather than state: it opens without JavaScript, which matters
          on a page whose whole point is being readable before React runs, and it
          is what a crawler reads. The marker is removed and replaced with an
          icon that rotates - group-open is how Tailwind reaches the open state.
        */}
        <div className="mt-8 divide-y divide-border border-y border-border">
          {FAQ.map((item, i) => (
            <details key={item.q} className={`group bloom-rise bloom-rise-${Math.min(i + 1, 4)}`}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-sm font-medium tracking-tight transition-colors hover:text-primary [&::-webkit-details-marker]:hidden">
                {item.q}
                <Plus className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-45" />
              </summary>
              <p className="max-w-[68ch] pb-5 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── Close ────────────────────────────────────────────────────────── */}
      <section>
        <div className="bloom-rise bg-primary text-primary-foreground rounded-2xl px-7 py-12 md:px-14 md:py-16 md:flex md:items-center md:gap-12">
          <div className="min-w-0 md:flex-1">
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.05] max-w-lg"
            style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
          >
            Put the whole stack in one place.
          </h2>
          <p className="mt-4 opacity-80 max-w-md leading-relaxed">
            Downtime, security patches, expiring certificates, backups nobody has tested, a server
            that cannot take the traffic — each one has a schedule on the platform rather than a
            place in somebody's week. Pick a package and start with one application.
          </p>
          {/*
            The page's last word used to be "Start a conversation", which
            undid the hero: a reader who had just been told this is a product
            arrived at the bottom and was offered a meeting. Signing up leads
            here too, with pricing beside it for the reader who wants the
            figure before the form.
          */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <Button
              size="lg"
              variant="secondary"
              className="px-7 rounded-full gap-2"
              onClick={() => navigate('/auth/register')}
            >
              Get started <ArrowRight className="h-4 w-4" />
            </Button>
            <Link
              to="/pricing"
              className="text-sm font-medium underline-offset-4 hover:underline opacity-90"
            >
              See the packages
            </Link>
          </div>
          </div>
          {/* Hidden on small screens: below the text it reads as filler, and the
              block is already tall on a phone. */}
          <WatchedStack className="hidden md:block w-56 shrink-0 opacity-90" />
        </div>
      </section>
    </div>
  );
}
